import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core'
import { DismissButtonComponent } from '../../icon-buttons/dismiss-button/dismiss-button.component'
import { TooltipDirective } from '../../../directives/tooltip/tooltip.directive'
import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import { ActionDropdownButtonComponent } from '../action-dropdown-button/action-dropdown-button.component'
import { BadgeComponent, BadgeType } from '../../data-display/badge/badge.component'
import type { BadgeSize } from '@awork/_shared/types/size'
import { Size } from '@awork/_shared/types/size'
import type { BadgeColor } from '@awork/_shared/types/color'
import { Color } from '@awork/_shared/types/color'
import type { ActionButtonSize, ActionButtonType } from './types'
import type { ActionDropdownButtonSize } from '../action-dropdown-button/types'
import type { IconButtonSize } from '../../icon-buttons/icon-button/types'
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common'
import type { Icon } from '@awork/_shared/static-data/icons'
import type { LucideIcon } from '@awork/_shared/directives/icons/types'

@Component({
  selector: 'aw-action-button',
  templateUrl: './action-button.component.html',
  styleUrls: ['./action-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DismissButtonComponent,
    TooltipDirective,
    IconsDirective,
    NgOptimizedImage,
    NgTemplateOutlet,
    ActionDropdownButtonComponent,
    BadgeComponent
  ]
})
export class ActionButtonComponent {
  readonly icon = input<string>()
  readonly image = input<string>()
  readonly roundImage = input(false)
  readonly text = input<string>()
  readonly counter = input<number | string>()
  readonly type = input<ActionButtonType>('border')
  readonly size = input<ActionButtonSize>(Size.m)
  readonly selected = input(false)
  readonly disabled = input(false)
  readonly removable = input(false)
  readonly disableRemove = input(false)
  readonly removeTooltip = input<string>(q.translations.common.clear)
  readonly withDropdown = input(false)
  readonly dropdownTooltip = input<string>()
  readonly tooltip = input<string>()
  readonly tooltipFromTop = input(false)
  readonly ariaLabel = input<string>()
  readonly ariaExpanded = input<boolean>()
  readonly ariaControls = input<string>()
  readonly inRow = input(false)

  readonly onClick = output<Event>()
  readonly onRemove = output<MouseEvent>()
  readonly onDropdownClick = output<MouseEvent>()

  readonly removeButtonHovered = signal(false)
  protected readonly Size = Size
  protected readonly BadgeType = BadgeType

  readonly buttonClasses = computed(() => this.buildButtonClasses())
  readonly removeButtonSize = computed(() => this.getRemoveButtonSize())
  readonly dropdownSize = computed(() => this.getDropdownSize())
  readonly badgeSize = computed(() => this.getBadgeSize())
  readonly badgeColor = computed(() => this.getBadgeColor())
  readonly imageSize = computed(() => this.getImageSize())
  readonly showRemoveButton = computed(() => this.removable() && !this.disableRemove() && !this.disabled())
  readonly showCounter = computed(() => this.counter() !== undefined && this.counter() !== null)
  readonly castedIcon = computed(() => this.castIcon(this.icon()))

  /**
   * Handle keyboard events (Enter or Space)
   * @param {Event} event The keyboard event that triggered the button
   */
  onKeydown(event: Event): void {
    event.preventDefault()
    event.stopPropagation()

    if (!this.disabled()) {
      this.onClick.emit(event)
    }
  }

  /**
   * Handle mouse click
   * @param {Event} event The mouse event that triggered the button
   */
  onButtonClick(event: Event): void {
    event.preventDefault()

    if (!this.disabled()) {
      this.onClick.emit(event)
    }
  }

  /**
   * Handle remove button click
   * @param {Event} event
   */
  onRemoveClick(event: Event): void {
    this.removeButtonHovered.set(false)
    this.onRemove.emit(event as MouseEvent)
    event.stopPropagation()
  }

  /**
   * Builds the CSS class string for the button container
   * @returns {string} Space-separated CSS class string
   */
  private buildButtonClasses(): string {
    const classes: string[] = ['action-button']

    classes.push(`action-button--size-${this.size()}`)

    classes.push(`action-button--${this.type()}`)

    if (this.selected()) {
      classes.push('action-button--selected')
    }

    if (this.disabled()) {
      classes.push('action-button--disabled')
    }

    if ((this.icon() || this.image()) && !this.text() && this.counter() === undefined) {
      classes.push('action-button--icon-only')
    }

    if (this.withDropdown()) {
      classes.push('action-button--with-dropdown')
    }

    if (this.inRow()) {
      classes.push('action-button--row')
    }

    return classes.join(' ')
  }

  /**
   * Handle dropdown button click
   * @param {MouseEvent} event
   */
  handleDropdownClick(event: MouseEvent): void {
    this.onDropdownClick.emit(event)
  }

  /**
   * Maps action button size to remove button size.
   * @returns {IconButtonSize} value for the remove button.
   */
  private getRemoveButtonSize(): IconButtonSize {
    const sizeMap: Record<ActionButtonSize, IconButtonSize> = {
      [Size.m]: Size.s,
      [Size.s]: Size.xs,
      [Size.xs]: Size.xs,
      [Size.xxs]: Size.xs
    }
    return sizeMap[this.size()]
  }

  /**
   * Maps action button size to dropdown button size
   * Note: xxs maps to xs since dropdown doesn't support xxs
   * @returns {ActionDropdownButtonSize} value for the dropdown button
   */
  private getDropdownSize(): ActionDropdownButtonSize {
    const sizeMap: Record<ActionButtonSize, ActionDropdownButtonSize> = {
      [Size.m]: Size.m,
      [Size.s]: Size.s,
      [Size.xs]: Size.xs,
      [Size.xxs]: Size.xs
    }
    return sizeMap[this.size()]
  }

  /**
   * Maps action button size to badge size
   * @returns {BadgeSize} value for the badge
   */
  private getBadgeSize(): BadgeSize {
    const sizeMap: Record<ActionButtonSize, BadgeSize> = {
      [Size.m]: Size.s,
      [Size.s]: Size.xs,
      [Size.xs]: Size.xs,
      [Size.xxs]: Size.xs
    }
    return sizeMap[this.size()]
  }

  /**
   * Maps action button type to badge color
   * @returns {BadgeColor} value for the badge
   */
  private getBadgeColor(): BadgeColor {
    return this.type() === 'primary' || this.selected() ? Color.Main : Color.Steel
  }

  /**
   * Maps action button size to image dimensions in pixels.
   * Mirrors the icon-size values used in action-button.component.scss so the
   * NgOptimizedImage width/height attributes match the rendered size.
   * @returns {number} edge length in pixels
   */
  private getImageSize(): number {
    const sizeMap: Record<ActionButtonSize, number> = {
      [Size.m]: 16,
      [Size.s]: 12,
      [Size.xs]: 12,
      [Size.xxs]: 10
    }
    return sizeMap[this.size()]
  }

  /**
   * Casts the icon name to the icon directive input type.
   * @param {string} iconName
   * @returns {Icon | LucideIcon | ''}
   */
  private castIcon(iconName?: string): Icon | LucideIcon | '' {
    return (iconName ?? '') as Icon | LucideIcon | ''
  }
}
